import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { PsfColors } from '../theme';
import { CurvePoint, MAX_CURVE_PCT } from '../lib/adjustments';

interface DepreciationCurveProps {
  curve: CurvePoint[];
  /** Seeded methodology curve, shown dashed for reference once the user edits. */
  referenceCurve?: CurvePoint[] | null;
  onChange: (curve: CurvePoint[]) => void;
  c: PsfColors;
  height?: number;
}

const PAD = { top: 14, right: 12, bottom: 26, left: 34 };
const VIEW_W = 320;

/**
 * Editable piecewise-linear remaining-value curve. Drag any point vertically to
 * change remaining value % vs brand-new at that age. Ages are fixed control
 * points so the graph stays aligned with the table editor.
 */
export default function DepreciationCurve({
  curve, referenceCurve, onChange, c, height = 190,
}: DepreciationCurveProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const sorted = useMemo(() => [...curve].sort((a, b) => a.age - b.age), [curve]);
  const maxAge = sorted.length > 0 ? sorted[sorted.length - 1].age : 60;
  const maxPct = MAX_CURVE_PCT;

  const xFor = (age: number) => PAD.left + (age / maxAge) * (VIEW_W - PAD.left - PAD.right);
  const yFor = (pct: number) => PAD.top + (1 - pct / maxPct) * (height - PAD.top - PAD.bottom);
  const pctForY = (y: number) => {
    const t = 1 - (y - PAD.top) / (height - PAD.top - PAD.bottom);
    return Math.max(0, Math.min(maxPct, t * maxPct));
  };

  const linePath = sorted.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(p.age)},${yFor(p.pct)}`).join(' ');
  const areaPath = sorted.length > 0
    ? `${linePath} L${xFor(sorted[sorted.length - 1].age)},${yFor(0)} L${xFor(sorted[0].age)},${yFor(0)} Z`
    : '';
  const refSorted = referenceCurve ? [...referenceCurve].sort((a, b) => a.age - b.age) : null;
  const refPath = refSorted
    ? refSorted.map((p, i) => `${i === 0 ? 'M' : 'L'}${xFor(p.age)},${yFor(p.pct)}`).join(' ')
    : null;

  const svgPointFromEvent = (e: React.PointerEvent): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * VIEW_W,
      y: ((e.clientY - rect.top) / rect.height) * height,
    };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragIndex == null) return;
    const pt = svgPointFromEvent(e);
    if (!pt) return;
    const pct = Math.round(pctForY(pt.y) * 10) / 10;
    const dragged = sorted[dragIndex];
    onChange(curve.map((p) => (p.age === dragged.age ? { ...p, pct } : p)));
  };

  const endDrag = () => setDragIndex(null);

  const activeIndex = dragIndex ?? hoverIndex;
  const activePoint = activeIndex != null ? sorted[activeIndex] : null;

  const gridPcts = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * maxPct));
  // Label a readable subset of ages — every point gets crowded past age 60.
  const gridAges = sorted.map((p) => p.age).filter((age, i, arr) =>
    age % 20 === 0 || age === arr[arr.length - 1]);

  return (
    <Box>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${height}`}
        style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'none', userSelect: 'none' }}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {/* Grid */}
        {gridPcts.map((p) => (
          <g key={`gy-${p}`}>
            <line x1={PAD.left} x2={VIEW_W - PAD.right} y1={yFor(p)} y2={yFor(p)} stroke={c.border} strokeWidth={0.6} />
            <text x={PAD.left - 5} y={yFor(p) + 3} fontSize={7.5} fill={c.textMuted} textAnchor="end">{p}%</text>
          </g>
        ))}
        {gridAges.map((age) => (
          <text key={`gx-${age}`} x={xFor(age)} y={height - PAD.bottom + 12} fontSize={7.5} fill={c.textMuted} textAnchor="middle">
            {age}
          </text>
        ))}
        <text x={(PAD.left + VIEW_W - PAD.right) / 2} y={height - 3} fontSize={7.5} fill={c.textMuted} textAnchor="middle">
          Building age (years)
        </text>

        {/* Reference (methodology) curve when edited */}
        {refPath && (
          <path d={refPath} fill="none" stroke={c.textMuted} strokeWidth={1} strokeDasharray="4 3" opacity={0.6} />
        )}

        {/* Area + line */}
        <path d={areaPath} fill={c.orange} opacity={0.12} />
        <path d={linePath} fill="none" stroke={c.orange} strokeWidth={2} strokeLinejoin="round" />

        {/* Draggable points */}
        {sorted.map((p, i) => (
          <g key={p.age}>
            {/* generous invisible hit target */}
            <circle
              cx={xFor(p.age)}
              cy={yFor(p.pct)}
              r={10}
              fill="transparent"
              style={{ cursor: 'ns-resize' }}
              onPointerDown={(e) => {
                (e.target as Element).setPointerCapture?.(e.pointerId);
                setDragIndex(i);
              }}
              onPointerEnter={() => setHoverIndex(i)}
              onPointerLeave={() => setHoverIndex((h) => (h === i ? null : h))}
            />
            <circle
              cx={xFor(p.age)}
              cy={yFor(p.pct)}
              r={activeIndex === i ? 5 : 3.5}
              fill={activeIndex === i ? c.yellow : c.surfaceCard}
              stroke={c.orange}
              strokeWidth={1.8}
              pointerEvents="none"
            />
          </g>
        ))}

        {/* Drag/hover tooltip */}
        {activePoint && (
          <g pointerEvents="none">
            <rect
              x={Math.min(VIEW_W - PAD.right - 78, Math.max(PAD.left, xFor(activePoint.age) - 39))}
              y={Math.max(1, yFor(activePoint.pct) - 24)}
              width={78}
              height={16}
              rx={3}
              fill={c.navyDark}
              opacity={0.92}
            />
            <text
              x={Math.min(VIEW_W - PAD.right - 78, Math.max(PAD.left, xFor(activePoint.age) - 39)) + 39}
              y={Math.max(1, yFor(activePoint.pct) - 24) + 11}
              fontSize={8}
              fill="#fff"
              textAnchor="middle"
            >
              Age {activePoint.age} · {activePoint.pct}%
            </text>
          </g>
        )}
      </svg>
      <Typography variant="caption" sx={{ color: c.textMuted, fontSize: '0.62rem', display: 'block', textAlign: 'center' }}>
        Drag any point up/down to fine-tune remaining value at that age
      </Typography>
    </Box>
  );
}
